import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import { translator } from 'mage-i18n';
import { SpacerHorizontal, SpacerVertical, Typography } from '@klarna/bubble-ui';
import { defaultContainerPropTypes, HorizontalWrapper, moetPropTypes } from 'moet-core';

import { generateCyElemId } from 'moet-test-utils/cypress/utils';
import { getDefaultState } from '../selectors';

import { CY_STAKEHOLDER_CONTAINER_IDS } from '@/constants/stakeholders';
import { CAPS } from '@/constants';
import { stakeholderUtils, withStakeholderLogic } from '@/containers/withStakeholderLogic';
import {
  BackButton,
  CallToActionButton,
  ContainerTemplate,
  StakeholderFormCard,
  ToggleWrappedContent,
} from '@/components';
import { logEvent } from '@/utils/analytics';

import { ANALYTICS_EVENTS } from '../analyticsEvents';

const tPrefix = 'products-fe.containers.managed_artificial_ultimate_beneficial_owner';

export class ManagedCeo extends React.Component {
  constructor(props) {
    super(props);
    const { toggleValue } = props.defaultState;

    this.state = { ctaClicked: false, toggleValue };

    this.onToggle = this.onToggle.bind(this);
    this.onNext = this.onNext.bind(this);
  }

  onToggle(toggleValue) {
    this.setState({ toggleValue });
  }

  onNext() {
    const { toggleValue } = this.state;
    const { stakeholders, mid, onNextWrapper, roleConstraints } = this.props;
    let stakeholdersToSubmit = [...stakeholders];

    this.setState({ ctaClicked: true });

    if (toggleValue) {
      stakeholdersToSubmit = [];
    }

    onNextWrapper(
      {
        payload: {
          params: {
            mid,
          },
          body: stakeholderUtils.getStakeholdersMappedValues(stakeholdersToSubmit),
        },
      },
      toggleValue !== undefined &&
        stakeholderUtils.isStakeholderConstraintsValid(stakeholdersToSubmit, roleConstraints) &&
        stakeholderUtils.isStakeholdersValid(stakeholdersToSubmit),
    );
  }

  render() {
    const { ctaClicked, toggleValue } = this.state;
    const {
      availableRoles = [],
      error,
      hasBack,
      isDisabled,
      isLoading,
      onBack,
      onBlurStakeholder,
      onChangeStakeholder,
      onTouchPersonalId,
      roleConstraints,
      stakeholders,
      t,
    } = this.props;

    const content = (
      <>
        <Typography textToken="text-style/headings/titles/bold/grande">
          {t(`${tPrefix}.title_stakeholder`)}
        </Typography>
        <SpacerVertical spaceToken="space/150" />
        <Typography
          textToken="text-style/text/super-paragraphs/body/medium"
          colorToken="colors/text/neutral"
        >
          {t(`${tPrefix}.subtitle_stakeholder`)}
        </Typography>
        <SpacerVertical spaceToken="space/300" />
        {stakeholders.map((stakeholder, index) => {
          return (
            <React.Fragment key={`ceo-${stakeholder.reactFormId}`}>
              <SpacerVertical spaceToken="space/150" />
              <StakeholderFormCard
                autoFocus
                tabIndexStart={1}
                stakeholder={stakeholder}
                availableRoles={availableRoles}
                disabledRoles={stakeholderUtils.getDisabledRoles(
                  roleConstraints,
                  stakeholders,
                  stakeholder.roles,
                )}
                onBlur={key => onBlurStakeholder(index, key)}
                onChange={(key, value) => onChangeStakeholder(index, key, value)}
                onTouchPersonalId={() => onTouchPersonalId(index)}
                isDisabled={isDisabled}
                name={`managed-ceo-${index}`}
              />
              <SpacerVertical spaceToken="space/600" />
            </React.Fragment>
          );
        })}
      </>
    );

    return (
      <ContainerTemplate
        title={t(`${tPrefix}.title`)}
        error={error}
        subtitle={t(`${tPrefix}.subtitle`)}
      >
        <ToggleWrappedContent
          error={ctaClicked && toggleValue === undefined}
          toggleOnChange={this.onToggle}
          toggleValue={toggleValue}
          noContent={content}
          isDisabled={isDisabled}
          commonContent={
            <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
              {hasBack && (
                <>
                  <BackButton tabIndex={10} onClick={onBack} disabled={isDisabled}>
                    {t(`${tPrefix}.buttons.back`)}
                  </BackButton>
                  <SpacerHorizontal spaceToken="space/300" />
                </>
              )}
              <CallToActionButton
                tabIndex={9}
                onClick={this.onNext}
                disabled={isDisabled}
                loading={isLoading}
                {...generateCyElemId(CY_STAKEHOLDER_CONTAINER_IDS.BTN_SUBMIT, window)}
              >
                {t(`${tPrefix}.buttons.next`)}
              </CallToActionButton>
            </HorizontalWrapper>
          }
        />
      </ContainerTemplate>
    );
  }
}

ManagedCeo.propTypes = {
  ...defaultContainerPropTypes,
  ...moetPropTypes.defaultStakeholderContainerPropTypes,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  defaultState: PropTypes.shape({
    toggleValue: PropTypes.bool,
  }),
};

const WrappedComponent = compose(
  connect(state => ({
    t: translator(state),
    defaultState: getDefaultState(state),
  })),
  withStakeholderLogic(CAPS.V2_MERCHANTS_STAKEHOLDERS_PUT, {
    capabilityCallback: ({ payload }) => {
      // rest of the data includes passport number and birthday
      if (payload.body && payload.body[0]) {
        let { type, roles } = payload.body[0];
        roles = roles.join(',');
        logEvent(ANALYTICS_EVENTS.submitCeo, { type, roles });
      }
    },
  }),
)(ManagedCeo);

// TODO: Create a "StepVariant" HOC that accepts these as options.
// Declarative definition of where the variant is used
WrappedComponent.onboardingTypes = ['MANAGED'];
WrappedComponent.deploymentRegionsAndCountries = {
  // For the US flow, we do not check for public companies before, so it needs to checked here
  us: ['US'],
};

export default WrappedComponent;
